/*
 * TEFCO — component behaviour (Step 2).
 * Source: TEFCO_HANDOFF/designs/TEFCO Components - States.dc.html
 *
 * Exposes window.TEFCO with: toast(), confirm(), openDrawer(), closeOverlay(),
 * refreshCart(). Everything degrades: with JS off the cart page, search results
 * page and full nav all still work — these are enhancements only.
 */
(function () {
  'use strict';

  var TEFCO = window.TEFCO || {};
  window.TEFCO = TEFCO;

  /* ---------------- overlay plumbing (focus trap + esc + scroll lock) ---------------- */
  var lastFocus = null;

  function openOverlay(el) {
    if (!el) return;
    lastFocus = document.activeElement;
    el.setAttribute('data-open', 'true');
    el.removeAttribute('aria-hidden');
    document.documentElement.style.overflow = 'hidden';
    var focusable = el.querySelector('[autofocus], button, [href], input, select, textarea');
    if (focusable) setTimeout(function () { focusable.focus(); }, 60);
  }

  function closeOverlay(el) {
    if (!el) return;
    el.setAttribute('data-open', 'false');
    el.setAttribute('aria-hidden', 'true');
    if (!document.querySelector('.tefco-ov[data-open="true"]')) {
      document.documentElement.style.overflow = '';
    }
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }
  TEFCO.closeOverlay = closeOverlay;
  // Exposed so the product gallery lightbox reuses the same focus trap,
  // Escape handling, scrim click and scroll lock as every other overlay.
  TEFCO.openOverlay = openOverlay;

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var open = document.querySelector('.tefco-ov[data-open="true"]');
    if (open) closeOverlay(open);
  });

  // Trap Tab inside an open overlay
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    var ov = document.querySelector('.tefco-ov[data-open="true"]');
    if (!ov) return;
    var f = Array.prototype.filter.call(
      ov.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'),
      function (n) { return n.offsetParent !== null; }
    );
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  // Any [data-tefco-close] or the scrim closes its overlay
  document.addEventListener('click', function (e) {
    var closer = e.target.closest('[data-tefco-close], .tefco-ov__scrim');
    if (!closer) return;
    var ov = closer.closest('.tefco-ov');
    if (ov) { e.preventDefault(); closeOverlay(ov); }
  });

  /* ---------------- toasts (5s auto-dismiss, errors persist) ---------------- */
  var ICONS = {
    success: '<svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="7.4" stroke="currentColor" stroke-width="1.7"/><path d="M6.6 10.2l2.3 2.3 4.5-4.7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    error:   '<svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="7.4" stroke="currentColor" stroke-width="1.7"/><path d="M10 6.3v5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><circle cx="10" cy="13.6" r="1" fill="currentColor"/></svg>',
    info:    '<svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="7.4" stroke="currentColor" stroke-width="1.7"/><path d="M10 9.4v4.3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><circle cx="10" cy="6.6" r="1" fill="currentColor"/></svg>'
  };

  function host() {
    var h = document.querySelector('.tefco-toasts');
    if (!h) {
      h = document.createElement('div');
      h.className = 'tefco-toasts';
      h.setAttribute('role', 'status');
      h.setAttribute('aria-live', 'polite');
      document.body.appendChild(h);
    }
    return h;
  }

  /**
   * TEFCO.toast({ type, title, text, actionText, actionHref })
   * type: 'success' | 'error' | 'info'. Errors never auto-dismiss.
   */
  TEFCO.toast = function (opts) {
    opts = opts || {};
    var type = opts.type === 'error' || opts.type === 'success' ? opts.type : 'info';
    var el = document.createElement('div');
    el.className = 'tefco-toast tefco-toast--' + type;
    if (type === 'error') el.setAttribute('role', 'alert');

    var action = opts.actionText && opts.actionHref
      ? '<a class="tefco-toast__action" href="' + opts.actionHref + '">' + opts.actionText + '</a>' : '';

    el.innerHTML =
      '<span class="tefco-toast__ico">' + ICONS[type] + '</span>' +
      '<span class="tefco-toast__main">' +
        '<span class="tefco-toast__title"></span>' +
        (opts.text ? '<span class="tefco-toast__text"></span>' : '') +
      '</span>' + action +
      '<button type="button" class="tefco-toast__close" aria-label="Dismiss">' +
        '<svg viewBox="0 0 16 16" width="13" height="13" fill="none" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>' +
      '</button>';

    // textContent (not innerHTML) so cart/product names can never inject markup
    el.querySelector('.tefco-toast__title').textContent = opts.title || '';
    if (opts.text) el.querySelector('.tefco-toast__text').textContent = opts.text;

    host().appendChild(el);

    function dismiss() { el.remove(); }
    el.querySelector('.tefco-toast__close').addEventListener('click', dismiss);
    if (type !== 'error') setTimeout(dismiss, 5000);   // errors persist
    return el;
  };

  /* ---------------- confirm modal (promise-based) ---------------- */
  TEFCO.confirm = function (opts) {
    opts = opts || {};
    var ov = document.querySelector('[data-tefco-confirm]');
    if (!ov) return Promise.resolve(window.confirm(opts.title || 'Are you sure?'));

    ov.querySelector('[data-confirm-title]').textContent = opts.title || 'Are you sure?';
    ov.querySelector('[data-confirm-body]').textContent = opts.body || '';
    var yes = ov.querySelector('[data-confirm-yes]');
    var no = ov.querySelector('[data-confirm-no]');
    yes.textContent = opts.confirmText || 'Confirm';
    no.textContent = opts.cancelText || 'Cancel';
    yes.className = 'tefco-modal__btn ' + (opts.danger ? 'tefco-modal__btn--danger' : 'tefco-modal__btn--confirm');

    openOverlay(ov);

    return new Promise(function (resolve) {
      function done(v) {
        yes.removeEventListener('click', onYes);
        no.removeEventListener('click', onNo);
        closeOverlay(ov);
        resolve(v);
      }
      function onYes() { done(true); }
      function onNo() { done(false); }
      yes.addEventListener('click', onYes);
      no.addEventListener('click', onNo);
    });
  };

  /* ---------------- cart drawer ---------------- */
  function drawer() { return document.querySelector('[data-tefco-cart-drawer]'); }

  TEFCO.openDrawer = function () { openOverlay(drawer()); };

  /** Re-render the drawer + header count from the Section Rendering API. */
  /* Re-render the cart PAGE in place via the Section Rendering API. Used after a
     /cart/change.js call when no drawer is open, so line totals, the subtotal and
     the VAT line all update without a full reload. Resolves quietly when the page
     has no cart section, so the shared handler never reports a false failure. */
  /* Cart PAGE refresh. Deliberately a reload, not a partial re-render.

     The Section Rendering API returned an empty body for this section, so the
     partial swap silently repainted nothing — the line stayed, the subtotal,
     VAT line and total never recomputed, and the page looked frozen. A reload
     is guaranteed correct for every total on the page, and because the quantity
     buttons are debounced it happens once after clicking stops, not per click.
     The drawer keeps its fast partial path via refreshCart(). */
  TEFCO.refreshCartPage = function () {
    var host = document.querySelector('[data-section-type="cart-main"]');
    if (!host) return Promise.resolve();
    window.location.reload();
    return new Promise(function () {});   // never settles; the reload takes over
  };

  TEFCO.refreshCart = function () {
    var d = drawer();
    if (!d || !window.fetch) return Promise.resolve();
    var id = d.getAttribute('data-section-id');
    var url = id ? '?section_id=' + id : null;

    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function (n) { n.textContent = cart.item_count; });
        if (!url) return cart;
        return fetch(url).then(function (r) { return r.text(); }).then(function (html) {
          var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-tefco-cart-drawer]');
          if (fresh) {
            var body = d.querySelector('.tefco-drawer__body');
            var freshBody = fresh.querySelector('.tefco-drawer__body');
            var foot = d.querySelector('.tefco-drawer__foot');
            var freshFoot = fresh.querySelector('.tefco-drawer__foot');
            var head = d.querySelector('[data-drawer-count]');
            var freshHead = fresh.querySelector('[data-drawer-count]');
            if (body && freshBody) body.innerHTML = freshBody.innerHTML;
            if (foot && freshFoot) foot.innerHTML = freshFoot.innerHTML;
            if (head && freshHead) head.textContent = freshHead.textContent;
          }
          return cart;
        });
      })
      .catch(function () {});
  };

  // Open the drawer whenever something reports an add
  document.addEventListener('tefco:cart:added', function () {
    TEFCO.refreshCart().then(function () { TEFCO.openDrawer(); });
  });

  // Open triggers
  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-tefco-drawer-open]');
    if (!t || !drawer()) return;
    e.preventDefault();
    TEFCO.refreshCart().then(TEFCO.openDrawer);
  });

  // Quantity / remove — drawer AND cart page. Talks to /cart/change.js.
  // Debounced per line: the input moves instantly and only the final value is
  // sent, so holding + does not queue a request (and a re-render) per click.
  var qtyTimers = {};
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-drawer-qty], [data-drawer-remove]');
    if (!btn || !window.fetch) return;
    // Resolve the ROW, not the button. The Remove anchor used to carry data-line
    // itself, so closest('[data-line]') stopped at the anchor and every lookup
    // below missed — which is why Remove appeared to do nothing.
    var row = btn.closest('[data-cart-line]') || btn.closest('[data-line]');
    if (!row) return;
    e.preventDefault();

    var line = row.getAttribute('data-line');
    var qty;
    if (btn.hasAttribute('data-drawer-remove')) {
      qty = 0;
    } else {
      var input = row.querySelector('[data-drawer-qty-input]');
      var step = parseInt(btn.getAttribute('data-drawer-qty'), 10) || 0;
      qty = Math.max(0, (parseInt(input && input.value, 10) || 1) + step);
      // Paint the new number now; the request follows once clicking stops.
      if (input) input.value = qty;
      row.style.opacity = '.55';
      if (qtyTimers[line]) clearTimeout(qtyTimers[line]);
      qtyTimers[line] = setTimeout(function () {
        delete qtyTimers[line];
        sendQty(row, line, qty);
      }, 450);
      return;
    }

    sendQty(row, line, qty);
  });

  function sendQty(row, line, qty) {
    // WHICH SURFACE is this change coming from? The cart drawer lives in the
    // layout, so it exists on EVERY page — testing `if (drawer())` sent cart-page
    // changes down the drawer path. The drawer repainted correctly, the cart page
    // never did, and Remove plus every total looked frozen. Decide from the row
    // that was actually clicked instead of from what happens to be in the DOM.
    var onCartPage = !!row.closest('[data-section-type="cart-main"]');
    var d = onCartPage ? null : drawer();
    if (d) d.setAttribute('aria-busy', 'true');
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: qty })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('change failed');
        return r.json();
      })
      .then(function () {
        // The drawer and the cart page share this handler. refreshCart() only
        // knows how to repaint the drawer and returns early without one, which
        // left the cart page silently stale. Re-render whichever is present.
        if (d) return TEFCO.refreshCart();
        return TEFCO.refreshCartPage();
      })
      .catch(function () {
        TEFCO.toast({ type: 'error', title: "Couldn't update quantity", text: 'Please try again, or open the cart page.' });
      })
      .then(function () {
        if (d) d.removeAttribute('aria-busy');
        if (row) row.style.opacity = '';
      });
  }

  /* ---------------- predictive search (3 chars, 250ms debounce) ---------------- */
  document.querySelectorAll('[data-tefco-ac]').forEach(function (wrap) {
    var input = wrap.querySelector('input[name="q"]');
    var panel = wrap.querySelector('[data-tefco-ac-panel]');
    if (!input || !panel || !window.fetch) return;

    var timer = null, controller = null;

    function hide() { panel.hidden = true; panel.innerHTML = ''; input.setAttribute('aria-expanded', 'false'); }

    function run(term) {
      if (controller) controller.abort();
      controller = new AbortController();
      var url = '/search/suggest?q=' + encodeURIComponent(term) +
                '&resources[type]=product,collection,query&resources[limit]=6&section_id=tefco-predictive';
      fetch(url, { signal: controller.signal })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var results = doc.querySelector('[data-tefco-ac-results]');
          if (results && results.innerHTML.trim()) {
            panel.innerHTML = results.innerHTML;
            panel.hidden = false;
            input.setAttribute('aria-expanded', 'true');
          } else {
            hide();
          }
        })
        .catch(function (err) { if (err.name !== 'AbortError') hide(); });
    }

    input.addEventListener('input', function () {
      var term = input.value.trim();
      clearTimeout(timer);
      if (term.length < 3) { hide(); return; }        // fires at 3 characters
      timer = setTimeout(function () { run(term); }, 250);   // debounce 250ms
    });

    // Keyboard: down/up move through rows, Esc closes
    input.addEventListener('keydown', function (e) {
      if (panel.hidden) return;
      var rows = Array.prototype.slice.call(panel.querySelectorAll('.tefco-ac__row'));
      if (!rows.length) return;
      var i = rows.indexOf(panel.querySelector('.tefco-ac__row.is-active'));
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (i > -1) rows[i].classList.remove('is-active');
        var n = e.key === 'ArrowDown' ? (i + 1) % rows.length : (i <= 0 ? rows.length - 1 : i - 1);
        rows[n].classList.add('is-active');
        rows[n].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter' && i > -1) {
        e.preventDefault();
        rows[i].click();
      } else if (e.key === 'Escape') {
        hide();
      }
    });

    document.addEventListener('click', function (e) { if (!wrap.contains(e.target)) hide(); });
  });

  /* ---------------- mobile menu ---------------- */
  document.addEventListener('click', function (e) {
    var open = e.target.closest('[data-tefco-menu-open]');
    if (open) {
      e.preventDefault();
      var mm = document.querySelector('[data-tefco-mobile-menu]');
      if (mm) { open.setAttribute('aria-expanded', 'true'); openOverlay(mm); }
      return;
    }
    // accordion groups
    var toggle = e.target.closest('[data-tefco-mm-toggle]');
    if (toggle) {
      e.preventDefault();
      var group = toggle.closest('.tefco-mm__group');
      if (group) {
        var isOpen = group.getAttribute('data-open') === 'true';
        group.setAttribute('data-open', isOpen ? 'false' : 'true');
        toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      }
    }
  });

  /* ---------------- cookie consent ---------------- */
  var CKEY = 'tefco_consent';

  function saveConsent(value) {
    try { localStorage.setItem(CKEY, value); } catch (e) {}
    // Hand the choice to Shopify's Customer Privacy API when it is present
    var api = window.Shopify && window.Shopify.customerPrivacy;
    if (api && api.setTrackingConsent) {
      var all = value === 'all';
      api.setTrackingConsent(
        { analytics: all, marketing: all, preferences: true, sale_of_data: all },
        function () {}
      );
    }
  }

  var banner = document.querySelector('[data-tefco-cookie]');
  if (banner) {
    var stored = null;
    try { stored = localStorage.getItem(CKEY); } catch (e) {}
    if (stored) {
      banner.remove();
    } else {
      banner.hidden = false;
      banner.addEventListener('click', function (e) {
        var b = e.target.closest('[data-consent]');
        if (!b) return;
        var choice = b.getAttribute('data-consent');
        if (choice === 'manage') {
          var panel = banner.querySelector('[data-consent-panel]');
          if (panel) {
            var shown = !panel.hidden;
            panel.hidden = shown;
            b.setAttribute('aria-expanded', shown ? 'false' : 'true');
          }
          return;
        }
        saveConsent(choice);
        banner.remove();
      });
    }
  }

  /* ---------------- form error focus ----------------
   * The accessibility statement claims forms "describe errors in words rather
   * than colour alone, and move focus to the first error on submit". Shopify
   * re-renders the page server-side after a failed submit, so on load we look
   * for an error summary and move focus to it, then fall back to the first
   * invalid control. Without this the claim would be aspirational.
   */
  function focusFirstError() {
    var summary = document.querySelector('.tefco-formsum');
    if (summary) {
      summary.focus({ preventScroll: false });
      return;
    }
    var invalid = document.querySelector('[aria-invalid="true"], .tefco-field--error .tefco-field__control');
    if (invalid && invalid.focus) invalid.focus();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', focusFirstError);
  } else {
    focusFirstError();
  }

  // Client-side submits: same behaviour without a page load.
  document.addEventListener(
    'invalid',
    function (e) {
      var form = e.target.form;
      if (!form || form.dataset.tefcoFocused) return;
      form.dataset.tefcoFocused = '1';
      setTimeout(function () {
        delete form.dataset.tefcoFocused;
      }, 0);
      if (e.target.focus) e.target.focus();
    },
    true // capture — 'invalid' does not bubble
  );

  /* ---------------- offline / online notices ---------------- */
  window.addEventListener('offline', function () {
    TEFCO.toast({
      type: 'error',
      title: "You're offline.",
      text: 'Cart changes are queued and will sync when the connection returns.'
    });
  });
})();

/* ===================== Wishlist (localStorage, no app needed) =====================
   The heart buttons ([data-tefco-wishlist][data-product-handle]) had no handler, so
   clicking did nothing. This module persists saved handles in localStorage, paints
   the hearts, keeps the header count in sync, and fills the wishlist page by fetching
   each saved product's card via the product.wishlist-card view. Delegated + re-paint
   hooks so it survives AJAX-rendered grids (collection filtering, wishlist page). */
(function () {
  var KEY = 'tefco:wishlist';
  function read() { try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { return []; } }
  function write(l) { try { localStorage.setItem(KEY, JSON.stringify(l)); } catch (e) {} }
  function has(h) { return read().indexOf(h) !== -1; }
  function toggle(h) { var l = read(), i = l.indexOf(h); if (i === -1) l.push(h); else l.splice(i, 1); write(l); return i === -1; }

  function updateCounts() {
    var n = read().length;
    document.querySelectorAll('[data-tefco-wishlist-count]').forEach(function (el) {
      el.textContent = n; el.classList.toggle('is-empty', n === 0);
    });
  }
  function paint(root) {
    (root || document).querySelectorAll('[data-tefco-wishlist]').forEach(function (b) {
      var on = has(b.getAttribute('data-product-handle'));
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }
  function renderPage() {
    var host = document.querySelector('[data-tefco-wishlist-page]');
    if (!host) return;
    var grid = host.querySelector('[data-tefco-wishlist-grid]');
    var empty = host.querySelector('[data-tefco-wishlist-empty]');
    if (!grid) return;
    var l = read();
    if (!l.length) { grid.innerHTML = ''; if (empty) empty.hidden = false; return; }
    if (empty) empty.hidden = true;
    grid.setAttribute('aria-busy', 'true');
    Promise.all(l.map(function (h) {
      return fetch('/products/' + encodeURIComponent(h) + '?view=wishlist-card')
        .then(function (r) { return r.ok ? r.text() : ''; }).catch(function () { return ''; });
    })).then(function (parts) {
      grid.innerHTML = parts.join('');
      grid.removeAttribute('aria-busy');
      paint(grid);
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-tefco-wishlist]');
    if (!btn) return;
    e.preventDefault();
    var h = btn.getAttribute('data-product-handle');
    if (!h) return;
    var saved = toggle(h);
    btn.classList.toggle('is-active', saved);
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    updateCounts();
    renderPage();
    if (window.TEFCO && TEFCO.toast) {
      TEFCO.toast({ type: saved ? 'success' : 'info', title: saved ? 'Saved to wishlist' : 'Removed from wishlist' });
    }
  });

  document.addEventListener('DOMContentLoaded', function () { paint(document); updateCounts(); renderPage(); });
  window.TEFCO = window.TEFCO || {};
  window.TEFCO.wishlistPaint = paint;
  window.TEFCO.wishlistRefresh = function () { updateCounts(); renderPage(); };
})();

/* ============ AJAX add-to-cart ============
   Product cards post to /cart/add with a plain form, so the browser followed the
   POST and dumped the shopper on /cart every time they added something. That is
   fine without JS, but with JS it should add quietly and let them keep browsing.

   Intercepts any form posting to /cart/add EXCEPT one submitted via the
   `checkout` button (Order now), which must keep its native behaviour so Shopify
   takes the shopper straight to checkout. Opens the drawer so the add is visible
   and reversible, and updates the header count. */
(function () {
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
    if (!window.fetch || !window.FormData) return;

    // Order now — let the native POST run so Shopify redirects to checkout.
    var sub = e.submitter || form.querySelector('button[type="submit"]:focus');
    if (sub && sub.name === 'checkout') return;

    e.preventDefault();
    var btn = sub || form.querySelector('button[type="submit"]');
    if (btn) btn.setAttribute('aria-busy', 'true');

    fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (j) { throw new Error(j && j.description || 'add failed'); });
        return r.json();
      })
      .then(function () {
        if (window.TEFCO && TEFCO.refreshCart) {
          return TEFCO.refreshCart().then(function () {
            if (TEFCO.openDrawer) TEFCO.openDrawer();
          });
        }
      })
      .catch(function (err) {
        if (window.TEFCO && TEFCO.toast) {
          TEFCO.toast({ type: 'error', title: "Couldn't add to cart", text: (err && err.message) || 'Please try again.' });
        }
      })
      .then(function () { if (btn) btn.removeAttribute('aria-busy'); });
  });

  /* ---------------- cart page: live shipping estimate ----------------
     "At checkout" was a dead string — a shopper could not see a delivery cost
     until they had already handed over an address. This asks Shopify for the
     rates the merchant has actually configured for the shop's own country and
     shows the cheapest. Every number comes from the merchant's rate table; if
     the call fails, or no zone covers the country, the Liquid fallback text is
     left exactly as rendered. The Total deliberately still excludes shipping —
     tax on shipping is a checkout calculation and must not be guessed here. */
  (function () {
    var el = document.querySelector('[data-cart-shipping]');
    if (!el || !window.fetch) return;
    var note    = document.querySelector('[data-cart-shipping-note]');
    var country = el.getAttribute('data-country');
    var cur     = el.getAttribute('data-currency') || 'SAR';
    if (!country) return;

    function money(v) {
      var n = parseFloat(v);
      if (isNaN(n)) return null;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency', currency: cur,
          minimumFractionDigits: 0, maximumFractionDigits: 2
        }).format(n);
      } catch (e) { return cur + ' ' + n; }
    }

    fetch('/cart/shipping_rates.json?shipping_address%5Bcountry%5D=' + encodeURIComponent(country),
          { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        var rates = d && d.shipping_rates;
        if (!rates || !rates.length) return;
        var cheapest = rates.reduce(function (a, b) {
          return parseFloat(b.price) < parseFloat(a.price) ? b : a;
        });
        var p = parseFloat(cheapest.price);
        var shown = money(cheapest.price);
        if (shown === null) return;
        el.textContent = p === 0 ? 'Free' : (rates.length > 1 ? 'from ' : '') + shown;
        if (note) {
          note.textContent = cheapest.name + ' — estimated for ' + country + '.';
          note.hidden = false;
        }
      })
      .catch(function () {});
  })();


  /* ---------------- product-card actions (Add to cart / Order now) ----------------
     These are plain buttons rather than <form>s on purpose: a product card can
     render inside another form — the collection filter form — and HTML forbids
     nested forms, so the browser was throwing the card's form away and closing
     the filter form with its </form>. Posting to /cart/add.js directly avoids the
     whole problem and behaves identically in every grid. */
  document.addEventListener('click', function (e) {
    // Scoped to BUTTONS carrying the card-specific attribute. The earlier selector
    // was [data-tefco-add], [data-tefco-buy] — and the product page's buy column is
    // <div class="tefco-pp-buy" data-tefco-buy>, which predates these buttons. So
    // closest() matched that wrapper for ANY click inside the column, cancelled it,
    // then bailed for want of a variant id: Add to Cart and Request a Quote both
    // died silently. Never widen this selector to a bare attribute again.
    var btn = e.target.closest('button[data-tefco-card-add], button[data-tefco-card-buy]');
    if (!btn || btn.disabled || !window.fetch) return;

    // Guard BEFORE preventDefault, so a mismatch can never swallow the click and
    // leave the native form submit cancelled with nothing sent in its place.
    var id = btn.getAttribute('data-variant-id');
    if (!id) return;

    e.preventDefault();
    var buyNow = btn.hasAttribute('data-tefco-card-buy');
    var label = btn.textContent;

    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (d) { throw new Error(d && d.description); });
        return r.json();
      })
      .then(function () {
        if (buyNow) {
          // Straight to checkout — leave the button busy, the page is going away.
          window.location.href = '/checkout';
          return;
        }
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        btn.textContent = label;
        document.dispatchEvent(new CustomEvent('tefco:cart:added'));
      })
      .catch(function (err) {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        btn.textContent = label;
        TEFCO.toast({
          type: 'error',
          title: "Couldn't add to cart",
          text: (err && err.message) || 'Please try again.'
        });
      });
  });

})();
